import React__default, { useId, useRef, useState, useEffect, useMemo, useLayoutEffect } from 'react';
import './SteppedProgressBar.scss';

const SteppedProgressBar = ({ steps, currentStepIndex, showLabels = true, className = '', }) => {
    const labelId = useId();
    const containerRef = useRef(null);
    const [windowWidth, setWindowWidth] = useState(0);
    const [internalShowLabels, setInternalShowLabels] = useState(showLabels);
    const [labelSizes, setLabelSizes] = useState({});
    const [containerWidth, setContainerWidth] = useState(0);
    // Simple resize handler
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        // Set initial width
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    // Calculate breakpoint width where labels overflow
    const breakpointWidth = useMemo(() => {
        if (!labelSizes || Object.keys(labelSizes).length === 0 || !containerWidth) {
            return Infinity;
        }
        // Calculate total width needed for all labels
        const totalLabelWidth = Object.values(labelSizes).reduce((sum, width) => sum + width, 0);
        // Add some padding/margin for spacing between labels
        const spacing = Object.keys(labelSizes).length * 20; // Adjust spacing as needed
        const totalNeededWidth = totalLabelWidth + spacing;
        return totalNeededWidth;
    }, [labelSizes, containerWidth]);
    // Update internal show labels based on cached breakpoint
    useEffect(() => {
        if (!showLabels) {
            setInternalShowLabels(false);
            return;
        }
        // If we have cached data, use it to determine if labels should show
        if (breakpointWidth !== Infinity && containerWidth > 0) {
            setInternalShowLabels(windowWidth >= breakpointWidth);
        }
    }, [showLabels, windowWidth, breakpointWidth, containerWidth]);
    const completionPercentage = Math.round(((currentStepIndex + 1) / steps.length) * 100);
    const halfStepPercentage = 100 / steps.length / 2;
    let alteredCompletionPercentage = Math.round((currentStepIndex / steps.length) * 100 + halfStepPercentage);
    if (currentStepIndex === 0) {
        alteredCompletionPercentage = halfStepPercentage;
    }
    if (currentStepIndex === steps.length - 1) {
        alteredCompletionPercentage = 100;
    }
    // Measure step labels and cache their sizes when steps change
    useLayoutEffect(() => {
        if (!containerRef.current || !showLabels) {
            return;
        }
        const container = containerRef.current;
        const stepLabels = container.querySelectorAll('.progress-bar-step-label--rev');
        // Update container width
        setContainerWidth(container.clientWidth);
        // Cache label sizes
        const newLabelSizes = {};
        stepLabels.forEach((label, index) => {
            const labelElement = label;
            newLabelSizes[`step-${index}`] = labelElement.scrollWidth;
        });
        setLabelSizes(newLabelSizes);
    }, [steps, showLabels]); // Only remeasure when steps change
    return (React__default.createElement("div", { ref: containerRef, "data-rev-component": 'stepped-progress-bar', role: 'meter progressbar', "aria-labelledby": labelId, "aria-valuenow": currentStepIndex, "aria-valuemin": 0, "aria-valuemax": steps.length - 1, "aria-valuetext": steps[currentStepIndex], className: className, style: {
            '--progressBar-percentage-complete': `${internalShowLabels ? alteredCompletionPercentage : completionPercentage}%`,
            '--progressBar-number-steps': steps.length,
        } },
        React__default.createElement("div", { className: 'progress-bar-titles--rev' },
            React__default.createElement("label", { id: labelId, className: 'progress-bar-label--rev' },
                "Step ",
                currentStepIndex + 1,
                " of ",
                steps.length),
            React__default.createElement("div", { className: 'progress-bar-completion-label--rev' },
                completionPercentage,
                "% complete")),
        React__default.createElement("div", { className: 'progress-bar-meter--rev' }),
        internalShowLabels && (React__default.createElement("ul", { className: 'progress-bar-labels--rev' }, steps.map((step, index) => (React__default.createElement("li", { key: index, className: 'progress-bar-step-label--rev', "data-rev-reached": index <= currentStepIndex }, step)))))));
};

export { SteppedProgressBar };
//# sourceMappingURL=SteppedProgressBar.js.map
